import React, { useState, useEffect, useRef, useCallback } from "react";
import { createChart } from "lightweight-charts";
import ApiClient from "../api/ApiClient";

export const DecisionMakerTuning = () => {
  const [tickerCode, setTickerCode] = useState("");
  const [searchedTickers, setSearchedTickers] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [decisionMakers, setDecisionMakers] = useState([]);
  const [selectedDecisionMaker, setSelectedDecisionMaker] = useState(null);
  const [options, setOptions] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [interval, setInterval] = useState("Day");
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [error, setError] = useState(null);
  const [candleData, setCandleData] = useState([]);
  const [simulationResult, setSimulationResult] = useState(null);
  const [highlightedNotification, setHighlightedNotification] = useState(null);
  const [highlightedTrade, setHighlightedTrade] = useState(null);

  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const containerRef = useRef(null);
  const clickHandlerRef = useRef(null);

  // Initialize with default date range (last year)
  useEffect(() => {
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);
    setDateFrom(formatDateForInput(oneYearAgo));
    setDateTo(formatDateForInput(now));
  }, []);

  // Load decision makers on mount
  useEffect(() => {
    async function loadDecisionMakers() {
      try {
        const data = await ApiClient.getDecisionMakers();
        setDecisionMakers(data);
      } catch (err) {
        console.error("Failed to load decision makers:", err);
        setError("Failed to load decision makers");
      }
    }
    loadDecisionMakers();
  }, []);

  // Search tickers when user types
  async function handleTickerSearch() {
    if (!tickerCode || tickerCode.length < 1) {
      setSearchedTickers([]);
      return;
    }

    try {
      const results = await ApiClient.searchTickerItems({
        SearchTerm: tickerCode,
        Limit: 20,
        Offset: 0
      });
      setSearchedTickers(results);
    } catch (err) {
      console.error("Failed to search tickers:", err);
      setError("Failed to search tickers");
    }
  }

  // Select ticker from search results
  function handleSelectTicker(ticker) {
    setSelectedTicker(ticker);
    setTickerCode(ticker.Code);
    setSearchedTickers([]);
    setSimulationResult(null); // Clear previous results
    loadCandleData(ticker);
  }

  // Load candle data for selected ticker
  async function loadCandleData(ticker) {
    if (!ticker || !dateFrom || !dateTo) return;

    try {
      setLoading(true);
      const response = await ApiClient.searchCandles({
        TickerId: ticker.Id,
        Interval: interval,
        TimeFrom: new Date(dateFrom).toISOString(),
        TimeTo: new Date(dateTo).toISOString(),
        Limit: 10000
      });
      setCandleData(response.Items || []);
    } catch (err) {
      console.error("Failed to load candle data:", err);
      setError("Failed to load candle data");
    } finally {
      setLoading(false);
    }
  }

  // Reload candles when date range or interval changes
  useEffect(() => {
    if (selectedTicker) {
      loadCandleData(selectedTicker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, interval]);

  // Handle decision maker selection
  function handleDecisionMakerChange(dmId) {
    const dm = decisionMakers.find(d => d.Id === dmId);
    setSelectedDecisionMaker(dm);
    setOptions(dm?.DefaultOptions || {});
  }

  // Handle options change
  function handleOptionsChange(field, value) {
    setOptions(prev => ({
      ...prev,
      [field]: value
    }));
  }

  // Run simulation
  async function handleSimulate() {
    if (!selectedTicker || !selectedDecisionMaker || !dateFrom || !dateTo) {
      setError("Please select ticker, decision maker, and date range");
      return;
    }

    try {
      setSimulating(true);
      setError(null);

      const body = {
        DecisionMaker: selectedDecisionMaker.Id,
        Options: options,
        DataToFeed: {
          TickersFilter: {
            Id: selectedTicker.Id
          },
          From: new Date(dateFrom).toISOString(),
          To: new Date(dateTo).toISOString()
        }
      };

      const result = await ApiClient.simulateDecisionMaker(body);
      setSimulationResult(result);
      setHighlightedNotification(null);
      setHighlightedTrade(null);
    } catch (err) {
      console.error("Simulation failed:", err);
      setError(`Simulation failed: ${err.message}`);
    } finally {
      setSimulating(false);
    }
  }

  // Setup click handler for chart markers
  const setupClickHandler = useCallback(() => {
    if (!chartRef.current || !simulationResult) return;

    // Remove previous click handler if exists
    if (clickHandlerRef.current) {
      chartRef.current.unsubscribeClick(clickHandlerRef.current);
    }

    const markers = [];

    // Get the first (and only) ticker data
    const tickerData = simulationResult.TickerData?.[0];
    if (!tickerData) return;

    // Add notification markers
    if (tickerData.Notifications) {
      tickerData.Notifications.forEach((notification, index) => {
        const timeUnix = notification.Time ? Math.floor(notification.Time.valueOf() / 1000) : 0;
        markers.push({
          time: timeUnix,
          id: `notification_${index}`,
          type: 'notification',
          index: index
        });
      });
    }

    // Add trade markers
    if (tickerData.Trades) {
      tickerData.Trades.forEach((trade, index) => {
        const timeUnix = trade.Time ? Math.floor(trade.Time.valueOf() / 1000) : 0;
        markers.push({
          time: timeUnix,
          id: `trade_${index}`,
          type: 'trade',
          index: index
        });
      });
    }

    const clickHandler = (param) => {
      if (param.time) {
        const clickTime = param.time;
        let closestMarker = null;
        let closestDistance = Infinity;

        markers.forEach(m => {
          const distance = Math.abs(m.time - clickTime);
          if (distance < closestDistance && distance <= 86400) {
            closestDistance = distance;
            closestMarker = m;
          }
        });

        if (closestMarker) {
          if (closestMarker.type === 'notification') {
            setHighlightedNotification(closestMarker.index);
            setHighlightedTrade(null);
            setTimeout(() => {
              const element = document.getElementById(`notification-${closestMarker.index}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          } else if (closestMarker.type === 'trade') {
            setHighlightedTrade(closestMarker.index);
            setHighlightedNotification(null);
            setTimeout(() => {
              const element = document.getElementById(`trade-${closestMarker.index}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }
        } else {
          setHighlightedNotification(null);
          setHighlightedTrade(null);
        }
      } else {
        setHighlightedNotification(null);
        setHighlightedTrade(null);
      }
    };

    chartRef.current.subscribeClick(clickHandler);
    clickHandlerRef.current = clickHandler;
  }, [simulationResult]);

  // Add markers to chart
  const addMarkersToChart = useCallback(() => {
    if (!candleSeriesRef.current || !simulationResult) return;

    const markers = [];
    const tickerData = simulationResult.TickerData?.[0];
    if (!tickerData) return;

    // Add notification markers
    if (tickerData.Notifications) {
      tickerData.Notifications.forEach((notification, index) => {
        const timeUnix = notification.Time ? Math.floor(notification.Time.valueOf() / 1000) : 0;
        const isHighlighted = highlightedNotification === index;
        markers.push({
          time: timeUnix,
          position: 'aboveBar',
          color: isHighlighted ? '#FF9800' : '#2196F3',
          shape: 'circle',
          size: isHighlighted ? 2 : 1
        });
      });
    }

    // Add trade markers
    if (tickerData.Trades) {
      tickerData.Trades.forEach((trade, index) => {
        const isBuy = trade.Quantity > 0;
        const timeUnix = trade.Time ? Math.floor(trade.Time.valueOf() / 1000) : 0;
        const isHighlighted = highlightedTrade === index;
        markers.push({
          time: timeUnix,
          position: isBuy ? 'belowBar' : 'aboveBar',
          color: isHighlighted ? '#FF9800' : (isBuy ? '#4CAF50' : '#F44336'),
          shape: isBuy ? 'arrowUp' : 'arrowDown',
          size: isHighlighted ? 2 : 1
        });
      });
    }

    if (markers.length > 0) {
      markers.sort((a, b) => a.time - b.time);
      candleSeriesRef.current.setMarkers(markers);
    }
  }, [simulationResult, highlightedNotification, highlightedTrade]);

  // Initialize chart
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 500,
      layout: { background: { color: "#ffffff" }, textColor: "#000" },
      rightPriceScale: { scaleMargins: { top: 0.1, bottom: 0.1 } },
      timeScale: { timeVisible: true, secondsVisible: false },
    });
    chartRef.current = chart;

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#4caf50",
      downColor: "#f44336",
      borderDownColor: "#f44336",
      borderUpColor: "#4caf50",
      wickDownColor: "#f44336",
      wickUpColor: "#4caf50",
    });
    candleSeriesRef.current = candleSeries;

    const handleResize = () => {
      if (!chartRef.current) return;
      chartRef.current.applyOptions({ width: container.clientWidth });
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartRef.current && clickHandlerRef.current) {
        chartRef.current.unsubscribeClick(clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, []);

  // Update chart data
  useEffect(() => {
    if (!candleSeriesRef.current || !candleData.length) return;

    const chartItems = candleData.map(candle => ({
      time: candle.getUnixSeconds(),
      open: Number(candle.Open),
      high: Number(candle.High),
      low: Number(candle.Low),
      close: Number(candle.Close),
    })).sort((a, b) => a.time - b.time);

    try {
      candleSeriesRef.current.setData(chartItems);
      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();
        addMarkersToChart();
        setupClickHandler();
      }
    } catch (err) {
      console.error("Failed to update chart:", err);
    }
  }, [candleData, addMarkersToChart, setupClickHandler]);

  // Update markers when highlights or simulation result changes
  useEffect(() => {
    if (candleSeriesRef.current && simulationResult) {
      addMarkersToChart();
    }
  }, [highlightedNotification, highlightedTrade, simulationResult, addMarkersToChart]);

  function formatDateForInput(date) {
    if (!date) return "";
    const d = new Date(date);
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  const tickerData = simulationResult?.TickerData?.[0];

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <h2>Decision Maker Tuning</h2>
          <p className="text-muted">
            Test and tune decision maker parameters in real-time with immediate simulation results.
          </p>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button type="button" className="close" onClick={() => setError(null)}>
                <span>&times;</span>
              </button>
            </div>
          )}

          {/* Configuration Section */}
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Configuration</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {/* Ticker Selection */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Ticker Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={tickerCode}
                      onChange={(e) => setTickerCode(e.target.value)}
                      onKeyUp={handleTickerSearch}
                      placeholder="Type ticker code..."
                    />
                    {searchedTickers.length > 0 && (
                      <div className="list-group mt-1" style={{ maxHeight: "200px", overflow: "auto" }}>
                        {searchedTickers.map((ticker) => (
                          <button
                            key={ticker.Id}
                            className="list-group-item list-group-item-action"
                            onClick={() => handleSelectTicker(ticker)}
                          >
                            <strong>{ticker.Code}</strong> - {ticker.Name}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedTicker && (
                      <small className="text-success">
                        ✓ Selected: {selectedTicker.Code} - {selectedTicker.Name}
                      </small>
                    )}
                  </div>
                </div>

                {/* Decision Maker Selection */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Decision Maker</label>
                    <select
                      className="form-control"
                      value={selectedDecisionMaker?.Id || ""}
                      onChange={(e) => handleDecisionMakerChange(e.target.value)}
                    >
                      <option value="">Select Decision Maker...</option>
                      {decisionMakers.map((dm) => (
                        <option key={dm.Id} value={dm.Id}>
                          {dm.Name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Interval Selection */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Interval</label>
                    <select
                      className="form-control"
                      value={interval}
                      onChange={(e) => setInterval(e.target.value)}
                    >
                      <option value="Minute">1 min</option>
                      <option value="FiveMinutes">5 min</option>
                      <option value="QuarterHour">15 min</option>
                      <option value="Hour">1 hr</option>
                      <option value="Day">Daily</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                {/* Date Range */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Options Section */}
          {selectedDecisionMaker && (
            <div className="card mb-3">
              <div className="card-header">
                <h5 className="mb-0">Decision Maker Options</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {Object.keys(options).map((key) => {
                    const value = options[key];
                    const displayKey = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())
                      .trim();

                    return (
                      <div key={key} className="col-md-4 mb-3">
                        <label>{displayKey}</label>
                        {typeof value === 'boolean' ? (
                          <select
                            className="form-control"
                            value={value.toString()}
                            onChange={(e) => handleOptionsChange(key, e.target.value === 'true')}
                          >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        ) : typeof value === 'number' ? (
                          <input
                            type="number"
                            className="form-control"
                            value={value}
                            step={value > 0 && value < 1 ? 0.01 : 1}
                            onChange={(e) => handleOptionsChange(key, parseFloat(e.target.value) || 0)}
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            value={value || ""}
                            onChange={(e) => handleOptionsChange(key, e.target.value)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="row">
                  <div className="col-12">
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={handleSimulate}
                      disabled={simulating || !selectedTicker || !selectedDecisionMaker}
                    >
                      {simulating ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          Simulating...
                        </>
                      ) : (
                        'Simulate'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Chart Section */}
          <div className="card mb-3">
            <div className="card-body">
              <div style={{ position: "relative", height: 500 }}>
                <div ref={containerRef} style={{ height: 500 }} />
                {loading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,0.8)",
                      zIndex: 1000,
                    }}
                  >
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          {simulationResult && tickerData && (
            <>
              {/* Summary */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-body text-center">
                      <h6 className="text-muted">Total Trades</h6>
                      <h3>{tickerData.Trades?.length || 0}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-body text-center">
                      <h6 className="text-muted">Total Notifications</h6>
                      <h3>{tickerData.Notifications?.length || 0}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-body text-center">
                      <h6 className="text-muted">Decision Maker</h6>
                      <h5>{simulationResult.DecisionMakerId}</h5>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Results */}
              <div className="row">
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Trades ({tickerData.Trades?.length || 0})</h6>
                    </div>
                    <div className="card-body" style={{ maxHeight: "400px", overflow: "auto" }}>
                      {tickerData.Trades?.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Time</th>
                                <th>Type</th>
                                <th>Qty</th>
                                <th>Price</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tickerData.Trades.map((trade, idx) => (
                                <tr
                                  key={idx}
                                  id={`trade-${idx}`}
                                  className={highlightedTrade === idx ? 'table-warning' : ''}
                                  style={{ cursor: 'pointer', transition: 'background-color 0.3s ease' }}
                                  onClick={() => {
                                    if (highlightedTrade === idx) {
                                      setHighlightedTrade(null);
                                    } else {
                                      setHighlightedTrade(idx);
                                      setHighlightedNotification(null);
                                    }
                                  }}
                                >
                                  <td>{trade.getTime()}</td>
                                  <td>
                                    <span className={`badge ${trade.Quantity > 0 ? 'badge-success' : 'badge-danger'}`}>
                                      {trade.Quantity > 0 ? 'BUY' : 'SELL'}
                                    </span>
                                  </td>
                                  <td>{trade.getAbsoluteQuantity()}</td>
                                  <td>{trade.getFormattedPrice()}</td>
                                  <td className={trade.TotalAmount >= 0 ? 'text-success' : 'text-danger'}>
                                    {trade.getFormattedAmount()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted">No trades executed</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Notifications ({tickerData.Notifications?.length || 0})</h6>
                    </div>
                    <div className="card-body" style={{ maxHeight: "400px", overflow: "auto" }}>
                      {tickerData.Notifications?.length > 0 ? (
                        <div className="list-group list-group-flush">
                          {tickerData.Notifications.map((notification, idx) => (
                            <div
                              key={idx}
                              id={`notification-${idx}`}
                              className={`list-group-item px-0 ${highlightedNotification === idx ? 'bg-warning' : ''}`}
                              style={{ cursor: 'pointer', transition: 'background-color 0.3s ease' }}
                              onClick={() => {
                                if (highlightedNotification === idx) {
                                  setHighlightedNotification(null);
                                } else {
                                  setHighlightedNotification(idx);
                                  setHighlightedTrade(null);
                                }
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <span className="badge text-muted mr-2">
                                    {notification.getTypeDisplay()}
                                  </span>
                                  <small className="text-muted">
                                    {notification.getTime()}
                                  </small>
                                </div>
                              </div>
                              <p className="mb-1 mt-2">{notification.getMessageDisplay()}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted">No notifications triggered</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DecisionMakerTuning;
